import { Delegate, ISubscription } from '../helpers/delegate';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';
import {
  CrosshairMode,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  MouseEventParams,
  PrimitivePaneViewZOrder,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IRange,
} from 'lightweight-charts';

export type TooltipPrimitiveOptions = {
  label: string;
  fontSize: number;
  crosshairRadius: number;
  markerColor: string;
  markerRadius: number;
  markerPadding: number;
  markerBackground: string;
};

export type TooltipCrosshairLineData = TooltipPrimitiveOptions & {
  visible: boolean;
  x: number;
  y: number;
  markerText: string;
};

const defaultOptions: TooltipPrimitiveOptions = {
  label: '',
  fontSize: 14,
  crosshairRadius: 4, // 4px
  markerRadius: 6,
  markerColor: '#ffffff',
  markerPadding: 4,
  markerBackground: 'rgba(19, 21, 23, 0.36)',
};

class TooltipCrosshairPaneRenderer implements IPrimitivePaneRenderer {
  _data: TooltipCrosshairLineData | undefined;

  constructor(data?: TooltipCrosshairLineData) {
    this._data = data;
  }

  draw(target: CanvasRenderingTarget2D) {
    if (!this._data || !this._data.visible) return;
    // const { x, y, fontSize, markerText, markerBackground, markerColor, crosshairRadius, markerPadding, markerRadius } =
    //   this._data;
    const { x, y, fontSize, markerBackground, markerColor, crosshairRadius } = this._data;
    target.useBitmapCoordinateSpace(scope => {
      const size = fontSize * scope.verticalPixelRatio;
      const ctx = scope.context;
      ctx.globalAlpha = 1;
      ctx.font = `${size}px Inter`;
      const markerX = x * scope.horizontalPixelRatio;
      const markerY = y * scope.verticalPixelRatio;
      const circleRadius = crosshairRadius * scope.verticalPixelRatio;
      // const padding = markerPadding * scope.horizontalPixelRatio;
      // const cornerRadius = markerRadius * scope.horizontalPixelRatio;
      // const markerTextWidth = ctx.measureText(markerText).width;

      // Draw circle
      ctx.beginPath();
      ctx.arc(markerX, markerY, circleRadius, 0, 2 * Math.PI);
      ctx.fillStyle = markerBackground;
      ctx.fill();
      ctx.strokeStyle = markerColor;
      ctx.lineWidth = 1.5 * scope.verticalPixelRatio;
      ctx.stroke();

      // // Draw series label
      // const labelWidth = markerTextWidth + padding * 2;
      // const isNearRightEdge = markerX + markerRadius + labelWidth + padding > scope.bitmapSize.width;
      // const isNearLeftEdge = markerX - markerRadius - labelWidth - padding < 0;

      // let labelX;
      // if (isNearRightEdge) {
      //   labelX = markerX - markerRadius - labelWidth - padding;
      // } else if (isNearLeftEdge) {
      //   labelX = markerX + markerRadius + padding;
      // } else {
      //   labelX = markerX + markerRadius + padding;
      // }

      // if (labelX < 0) {
      //   labelX = padding;
      // } else if (labelX + labelWidth > scope.bitmapSize.width) {
      //   labelX = scope.bitmapSize.width - labelWidth - padding;
      // }

      // // Draw rounded rectangle
      // ctx.fillStyle = markerBackground;
      // ctx.beginPath();
      // ctx.moveTo(labelX + cornerRadius, markerY - size / 2 - padding);
      // ctx.lineTo(labelX + labelWidth - cornerRadius, markerY - size / 2 - padding);
      // ctx.arcTo(
      //   labelX + labelWidth,
      //   markerY - size / 2 - padding,
      //   labelX + labelWidth,
      //   markerY - size / 2 - padding + cornerRadius,
      //   cornerRadius
      // );
      // ctx.lineTo(labelX + labelWidth, markerY + size / 2 + padding - cornerRadius);
      // ctx.arcTo(
      //   labelX + labelWidth,
      //   markerY + size / 2 + padding,
      //   labelX + labelWidth - cornerRadius,
      //   markerY + size / 2 + padding,
      //   cornerRadius
      // );
      // ctx.lineTo(labelX + cornerRadius, markerY + size / 2 + padding);
      // ctx.arcTo(labelX, markerY + size / 2 + padding, labelX, markerY + size / 2 + padding - cornerRadius, cornerRadius);
      // ctx.lineTo(labelX, markerY - size / 2 - padding + cornerRadius);
      // ctx.arcTo(labelX, markerY - size / 2 - padding, labelX + cornerRadius, markerY - size / 2 - padding, cornerRadius);
      // ctx.closePath();
      // ctx.fill();

      // Draw text
      // ctx.fillStyle = markerColor;
      // ctx.fillText(markerText, labelX + padding, markerY + size / 3);
    });
  }
}

class MultiTouchCrosshairPaneView implements IPrimitivePaneView {
  _data: TooltipCrosshairLineData | undefined;
  constructor(data?: TooltipCrosshairLineData) {
    this._data = data;
  }

  update(data?: TooltipCrosshairLineData): void {
    this._data = data;
  }

  renderer(): IPrimitivePaneRenderer | null {
    return new TooltipCrosshairPaneRenderer(this._data);
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'bottom';
  }
}

export class TooltipPrimitive implements ISeriesPrimitive<Time> {
  private _options: TooltipPrimitiveOptions;
  private _paneViews: MultiTouchCrosshairPaneView[];
  private _data: TooltipCrosshairLineData | undefined;
  private _attachedParams: SeriesAttachedParameter<Time> | undefined;
  private _activeRange: Delegate<IRange<number> | null> = new Delegate();

  constructor(options?: Partial<TooltipPrimitiveOptions>) {
    this._options = { ...defaultOptions, ...options };
    this._paneViews = [new MultiTouchCrosshairPaneView()];
  }

  updateAllViews() {
    this._paneViews.forEach(pw => pw.update(this._data));
  }

  paneViews() {
    return this._paneViews;
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._attachedParams = param;
    this._setCrosshairMode();
    param.chart.subscribeCrosshairMove(this._moveHandler);
  }

  detached(): void {
    this._attachedParams?.chart?.unsubscribeCrosshairMove(this._moveHandler);
    this._activeRange.destroy();
  }

  public activeRange(): ISubscription<IRange<number> | null> {
    return this._activeRange;
  }

  // diy
  setData(data?: TooltipCrosshairLineData) {
    this._data = data;
    this.updateAllViews();
    this._attachedParams?.requestUpdate();
  }

  chart() {
    return this._attachedParams?.chart;
  }

  applyOptions(options: Partial<TooltipPrimitiveOptions>) {
    this._options = {
      ...this._options,
      ...options,
    };
  }

  private _setCrosshairMode() {
    this._attachedParams?.chart?.applyOptions({
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { visible: false, labelVisible: false },
        horzLine: { visible: false, labelVisible: false },
      },
    });
  }

  private _moveHandler = (param: MouseEventParams<Time>) => {
    if (!this._attachedParams) return;
    const chart = this._attachedParams?.chart;
    const series = this._attachedParams.series;
    const data = param.seriesData.get(series);
    const logical = param.logical;
    if (!logical || !chart || !series || !data) {
      this._hideCrosshair();
      return;
    }
    const to = Number(
      this._attachedParams.chart.timeScale().getVisibleLogicalRange()?.to ?? Math.max(logical, series.data.length - 1)
    );
    const priceFormatter = chart.options().localization.priceFormatter ?? series.priceFormatter().format;
    const coordinateX = chart.timeScale().logicalToCoordinate(logical);
    const fontSize = chart.options().layout.fontSize;
    const options = series.options();
    //@ts-ignore
    const price = data?.value ?? 0;
    const coordinateY = series.priceToCoordinate(price);
    //@ts-ignore
    const color = options?.color || options.baseLineColor || options.priceLineColor || 'rgba(0, 0, 0, 1)';
    if (logical) {
      //@ts-ignore
      if (series.options()?.customRange?.from === logical) return;
      this._activeRange.fire({
        from: logical,
        to,
      });
    } else {
      this._activeRange.fire(null);
    }
    this.setData({
      ...this._options,
      fontSize,
      visible: options.visible != false && coordinateX !== null && coordinateY != null,
      x: coordinateX ?? 0,
      y: coordinateY ?? 0,
      markerBackground: color,
      markerText: `${this._options.label} ${priceFormatter(price)}`,
    });
  };

  private _hideCrosshair() {
    this.setData(undefined);
    this._activeRange.fire(null);
  }
}
